import React from 'react';
import { useDispatch } from 'react-redux';

import { Box, InputLabel, SvgIcon, TextField, TextFieldProps } from '@mui/material';
import { Field, FieldProps, Form, Formik, useFormikContext } from 'formik';
import { setCnDateRange } from 'reducers/contactNoteListReducer';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import MomentUtils from '@date-io/moment';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DesktopDateRangePicker } from '@mui/x-date-pickers-pro/DesktopDateRangePicker';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { NOOP } from 'utils/utils';

type Props = { startField: string; endField: string } & FieldProps;
type DatePickerFormFields = {
  [key: string]: string;
};

const DatePickerField = ({ startField, endField }: Props) => {
  const dispatch = useDispatch();

  const { errors, setFieldTouched, setFieldValue, touched, values } = useFormikContext<DatePickerFormFields>();

  const startDateError = errors[startField];
  const toShowStartDateError = Boolean(startDateError && touched[startField]);
  const endDateError = errors[endField];
  const toShowEndDateError = Boolean(endDateError && touched[endField]);

  const updateDateFilterState = (dateRange: DateRange<any>) => {
    dispatch(setCnDateRange(dateRange.map((date) => date?.utc(true).toISOString() || null)));
  };

  const handleChange = ([startDate, endDate]: DateRange<any>) => {
    setFieldValue(startField, startDate, false);
    setFieldValue(endField, endDate, false);
    updateDateFilterState([startDate, endDate]);
  };

  return (
    <LocalizationProvider
      dateAdapter={MomentUtils as any}
      dateFormats={{ normalDate: 'DD.MM.YYYY', keyboardDate: 'DD.MM.YYYY' }}
      localeText={{ start: '', end: '' }}
    >
      <InputLabel htmlFor="date-range-filter-from-textfield" error={toShowEndDateError}>
        Contact date
      </InputLabel>

      <DesktopDateRangePicker
        mask="__.__.____"
        value={[values[startField], values[endField]]}
        onChange={handleChange}
        renderInput={(startProps: TextFieldProps, endProps: TextFieldProps) => {
          return (
            <>
              <TextField
                {...startProps}
                name={startField}
                inputProps={{
                  ...startProps.inputProps,
                  autoComplete: 'off',
                }}
                id="date-range-filter-from-textfield"
                variant="filled"
                autoComplete="false"
                error={toShowStartDateError}
                fullWidth
                placeholder="dd.MM.yyyy"
                helperText={toShowStartDateError ? (startDateError ?? '') : undefined}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <SvgIcon sx={{ margin: '7px 8px 0 0' }}>
                      <CalendarIcon />
                    </SvgIcon>
                  ),
                }}
                onBlur={() => setFieldTouched(startField, true, false)}
              />
              <Box sx={{ mx: 2 }}> to </Box>
              <TextField
                {...endProps}
                fullWidth
                name={endField}
                id="date-range-filter-to-textfield"
                variant="filled"
                placeholder="dd.MM.yyyy"
                inputProps={{ ...endProps.inputProps, autoComplete: 'off' }}
                error={toShowEndDateError}
                helperText={toShowEndDateError ? (endDateError ?? '') : undefined}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <SvgIcon sx={{ margin: '7px 8px 0 0' }}>
                      <CalendarIcon />
                    </SvgIcon>
                  ),
                }}
                onBlur={() => setFieldTouched(endField, true, false)}
              />
            </>
          );
        }}
      />
    </LocalizationProvider>
  );
};

// We use Formik for the validation and other form goodies (field touched etc)
export const CustomDateRangePicker = () => {
  const startField = 'start';
  const endField = 'end';
  return (
    <Formik
      onSubmit={NOOP}
      initialValues={{
        [startField]: null,
        [endField]: null,
      }}
    >
      {() => (
        <Form>
          <Field name="date" startField={startField} endField={endField} clearable component={DatePickerField} />
        </Form>
      )}
    </Formik>
  );
};

export default CustomDateRangePicker;
